import { UpdateScheduleHandler } from './logicHandlers/UpdateScheduleHandler';
import { WatchlistHandler } from './logicHandlers/WatchlistHandler';
import { LiveToReplayHandler } from './logicHandlers/LiveToReplayHandler';
import { BookmarkHandler } from './logicHandlers/BookmarkHandler';
import { AssetType } from '../../api/models/Shared.types';

/**
 * @enum {string}
 */
export const PlayerEvent = {
  ENDED: 'ended',
  PAUSE: 'pause',
  PLAYING: 'playing',
  ERROR: 'error',
  BUFFERING_STARTED: 'bufferingstarted',
  LOADEDMETADATA: 'loadedmetadata',
  BUFFERING_ENDED: 'bufferingended',
};

/**
 * @typedef {Object} BookmarkInfo
 * @property {number} position
 * @property {number} offset
 * @property {AssetType} [assetType]
 * @property {string} [assetId]
 */
export interface BookmarkInfo {
  position: number;
  offset: number;
  assetType?: AssetType;
  assetId?: string;
}

/**
 * @typedef {Object} SeekbarInfo
 * @property {number} posPercent
 * @property {string} currTime
 * @property {string} remainTime
 */
export interface SeekbarInfo {
  posPercent: number;
  currTime: string;
  remainTime: string;
}

/**
 * @enum {string}
 */
export const StreamStatus = {
  IDLE: 'IDLE',
  PLAYING: 'PLAYING',
  PAUSED: 'PAUSED',
};

/**
 * @typedef {Object} PlayerTimes
 * @property {number} position
 * @property {number} duration
 */
export interface PlayerTimes {
  position: number;
  duration: number;
}

/**
 * @typedef {Object} VideoQuality
 * @property {string} id
 * @property {number} height
 * @property {number} [framerate]
 * @property {boolean} active
 */
export interface VideoQuality {
  id: string;
  height: number;
  framerate?: number;
  active: boolean;
}

/**
 * @typedef {Object} AudioTrack
 * @property {string} id
 * @property {string} [language]
 * @property {string} [label]
 * @property {number} [channelsCount]
 * @property {boolean} active
 */
export interface AudioTrack {
  id: string;
  language?: string;
  label?: string;
  channelsCount?: number;
  active: boolean;
}

/**
 * @typedef {Object} SubtitleTrack
 * @property {string} id
 * @property {string} [language]
 * @property {string} [label]
 * @property {string} [representationId]
 * @property {boolean} active
 */
export interface SubtitleTrack {
  id: string;
  language?: string;
  label?: string;
  representationId?: string;
  active: boolean;
}

export interface UIStateConfig {
  focusSeekbar: boolean;
  channelUp: boolean;
  channelDown: boolean;
}

/**
 * @enum {string}
 */
export const StreamOptions = {
  RESUME_FROM_LIVE: 'RESUME_FROM_LIVE',
};

export interface LogicHandlers {
  scheduleHandler: UpdateScheduleHandler | undefined;
  watchlistHandler: WatchlistHandler | undefined;
  liveToReplayHandler: LiveToReplayHandler | undefined;
  bookmarkHandler: BookmarkHandler | undefined;
}

export const drmProviders = {
  NONE: 'NONE',
  WIDEVINE: 'WIDEVINE',
  PLAYREADY: 'PLAYREADY',
  FAIRPLAY: 'FAIRPLAY',
  AES: 'AES', // TODO: no match for this in castlabs?
  OTHER: 'OTHER',
};

export const hdcpLevels = {
  NONE: 'NONE',
  NO_DIGITAL_OUTPUT: 'HDCP_NO_DIGITAL_OUTPUT',
  HDCP_1_X: 'HDCP_1_X',
  HDCP_2_X: 'HDCP_2_X',
  HDCP_V1: 'HDCP_V1',
  HDCP_V2_0: 'HDCP_V2_0',
  HDCP_V2_1: 'HDCP_V2_1',
  HDCP_V2_2: 'HDCP_V2_2',
  HDCP_V2_3: 'HDCP_V2_3',
};

/**
 * @typedef {Object} DrmAnalyticsProperties
 * @property {drmProviders}   [provider]
 * @property {number}   [outputWidth]
 * @property {number}   [outputHeight]
 * @property {boolean}   [supportsHdr10]
 * @property {string}   [securityLevel]
 */
export interface DrmAnalyticsProperties {
  provider?: string;
  outputWidth?: number;
  outputHeight?: number;
  supportsHdr10?: boolean;
  securityLevel?: string;
}

/**
 * @typedef {Object} SeekRange
 * @property {number} start
 * @property {number} end
 */
export interface SeekRange {
  start: number;
  end: number;
}
